import { queryOptions, skipToken } from "@tanstack/react-query";
import axios from "axios";

import { EHttpStatusCode, processRequest } from "@api/config";
import { type TGetSessionTokenResponse, toysAPI } from "@api/toys";

import { useToysStore } from "@storage/global/useToysStore";

import { useAuthQuery } from "@lib/queryClient";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";
import { STALE_INFINITY } from "@configs/constants";

import { resetToysSession } from "./resetToysSession";

interface IToysSessionTokenQueryOptions {
	enabled?: boolean;
	privateShowId?: string | null;
}

export const useToysSessionTokenQuery = ({
	enabled = true,
	privateShowId = null,
}: IToysSessionTokenQueryOptions = {}) => {
	const options = getToysSessionTokenQueryOptions(privateShowId);

	return useAuthQuery({
		...options,
		queryFn: enabled ? options.queryFn : skipToken,
	});
};

export const getToysSessionTokenQueryOptions = (privateShowId: string | null = null) =>
	queryOptions<TGetSessionTokenResponse>({
		queryKey: [QUERY_KEYS.TOYS_SESSION_TOKEN, privateShowId],
		queryFn: ({ client, signal }) =>
			processRequest({
				prefix: API_PREFIXES.GET_TOYS_SESSION_TOKEN,
				request: async (token) => {
					try {
						return await toysAPI.getSessionTokenAsync({ token, signal });
					} catch (error) {
						if (
							// Add this logic for case when session is closed and we try to get token.
							// return 404 status code
							!signal.aborted &&
							useToysStore.getState().activePrivateShowId === privateShowId &&
							axios.isAxiosError(error) &&
							error.response?.status === EHttpStatusCode.NOT_FOUND
						) {
							resetToysSession(client);
							throw new axios.Cancel("Toys session does not exist");
						}
						throw error;
					}
				},
			}),
		staleTime: STALE_INFINITY,
		gcTime: STALE_INFINITY,
		retry: false,
		retryOnMount: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
